import { styled } from "@mui/system";
import * as React from "react";
import { t } from "../../i18n/util";
import { BIAReportHistory, LifestyleReportHistory, PatientInfo } from "../../network/APITypes";
import { BiaValueDisplay } from "../patients/BiaValueDisplay";
import { MultiValueDisplay } from "../patients/MultiValueDisplay";
import { BMI_MAX_VALUE, BMI_MIN_VALUE } from "../util/constants";
import { formatDate, formatPatientName } from "../util/helpers";
import { getBmiDotPosition } from "../util/MultiValueDisplayHelpers";
import {
    getAbdominalGirthBreakpoints,
    getAbdominalGirthColor,
    getBiaDotPosition,
    getBiaValueColor,
    getBloodpressureColor,
    getBmiColor,
    getExerciseColor,
    getExerciseDropdownText,
    getHistoryHeaderItems,
    getSmileyByColor,
    getSmokerColor,
    getSmokerText,
} from "../util/ValueDisplayHelpers";
import { BloodpressureValueDisplay } from "./BloodpressureValueDisplay";
import { Card } from "./Card";
import { ConspicuousnessDisplay } from "./ConspicuousnessDisplay";
import { CurrentValueTable } from "./CurrentValueTable";
import { HistoryValuesTable } from "./HistoryValuesTable";
import { ValueGradient } from "./ValueGradient";
import { VerticalTableDivider } from "./VerticalTableDivider";

type IProps = {
    lifestyleReport?: LifestyleReportHistory;
    biaReport?: BIAReportHistory;
    patientInformation?: PatientInfo;
};

const Title = styled("div")({
    fontWeight: 600,
    fontSize: 18,
    marginBottom: 40,
});

export const LifestyleParametersHistory = ({ lifestyleReport, biaReport, patientInformation }: IProps) => {
    const currentValueHeaderItems = [
        {
            name: "name",
            topLabel: t("screen.history.table.measurement"),
        },
        {
            name: "value",
            topLabel: t("screen.history.table.currentValueLabel"),
            date: formatDate(lifestyleReport?.latest?.checkupDate ?? biaReport?.latest?.checkupDate),
            patientName: formatPatientName(patientInformation),
        },
        {
            name: "referenceRange",
            topLabel: t("screen.history.table.referenceRange"),
        },
    ];

    // Use BIA history for the structure since it has more complete data
    const { valueHeaderItems, latestHistoryItems, lastCheckupReport, emptyColumns } = getHistoryHeaderItems(
        biaReport?.history || lifestyleReport?.history,
        patientInformation,
    );

    // abdominalGirth
    const latestAbdominalGirthHistory = latestHistoryItems.map((checkupReport) => {
        // Find corresponding lifestyle data for this checkup date
        const lifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === checkupReport.checkupDate,
        );
        return {
            content: lifestyleData?.abdominalGirth ?? "",
            color: getAbdominalGirthColor(lifestyleData?.abdominalGirth, patientInformation?.sex),
            adornmentText: t("common.centimeter"),
        };
    });
    const lastAbdominalGirthItem = (() => {
        const lastLifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === lastCheckupReport?.checkupDate,
        );
        return {
            content: lastLifestyleData?.abdominalGirth ?? "",
            color: getAbdominalGirthColor(lastLifestyleData?.abdominalGirth, patientInformation?.sex),
            adornmentText: t("common.centimeter"),
        };
    })();
    const abdominalGirthSmiley = getSmileyByColor(
        getAbdominalGirthColor(lifestyleReport?.latest?.abdominalGirth, patientInformation?.sex),
    );
    const abdominalGirthRow = [
        { content: abdominalGirthSmiley },
        ...emptyColumns,
        ...latestAbdominalGirthHistory,
        lastAbdominalGirthItem,
    ];

    // exerciseDropdown
    const exerciseDropdownColor = getExerciseColor(lifestyleReport?.latest?.exerciseDropdown);

    const latestExerciseDropdownHistory = latestHistoryItems.map((checkupReport) => {
        // Find corresponding lifestyle data for this checkup date
        const lifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === checkupReport.checkupDate,
        );
        return {
            content: getExerciseDropdownText(lifestyleData?.exerciseDropdown ?? ""),
            color: getExerciseColor(lifestyleData?.exerciseDropdown),
        };
    });

    const lastExerciseDropdownItem = (() => {
        const lastLifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === lastCheckupReport?.checkupDate,
        );
        return {
            content: getExerciseDropdownText(lastLifestyleData?.exerciseDropdown ?? ""),
            color: getExerciseColor(lastLifestyleData?.exerciseDropdown),
        };
    })();

    const exerciseDropdownHistoryValuesRow = [
        { content: getSmileyByColor(exerciseDropdownColor) },
        ...emptyColumns,
        ...latestExerciseDropdownHistory,
        lastExerciseDropdownItem,
    ];

    // smokerDropdown
    const smokerDropdownColor = getSmokerColor(lifestyleReport?.latest?.smokerDropdown);

    const latestSmokerDropdownHistory = latestHistoryItems.map((checkupReport) => {
        // Find corresponding lifestyle data for this checkup date
        const lifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === checkupReport.checkupDate,
        );
        return {
            content: getSmokerText(lifestyleData?.smokerDropdown ?? ""),
            color: getSmokerColor(lifestyleData?.smokerDropdown),
        };
    });

    const lastSmokerDropdownItem = (() => {
        const lastLifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === lastCheckupReport?.checkupDate,
        );
        return {
            content: getSmokerText(lastLifestyleData?.smokerDropdown ?? ""),
            color: getSmokerColor(lastLifestyleData?.smokerDropdown),
        };
    })();

    const smokerDropdownHistoryValuesRow = [
        { content: getSmileyByColor(smokerDropdownColor) },
        ...emptyColumns,
        ...latestSmokerDropdownHistory,
        lastSmokerDropdownItem,
    ];

    // bmi
    const getBmi = (weight?: number, height?: number) => {
        if (weight && height) {
            return Math.round((weight / Math.pow(height / 100, 2)) * 100) / 100;
        }

        return undefined;
    };

    const latestWeightHistory = latestHistoryItems.map((checkupReport) => {
        const lifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === checkupReport.checkupDate,
        );
        return {
            content: lifestyleData?.weight ?? "",
            adornmentText: t("common.kilogram"),
        };
    });

    const lastWeightItem = (() => {
        const lastLifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === lastCheckupReport?.checkupDate,
        );
        return {
            content: lastLifestyleData?.weight ?? "",
            adornmentText: t("common.kilogram"),
        };
    })();

    const weightHistoryValuesRow = [{ content: "" }, ...emptyColumns, ...latestWeightHistory, lastWeightItem];

    const bmiColor = getBmiColor(getBmi(lifestyleReport?.latest?.weight, lifestyleReport?.latest?.height));

    const latestBmiHistory = latestHistoryItems.map((checkupReport) => {
        // Find corresponding lifestyle data for this checkup date
        const lifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === checkupReport.checkupDate,
        );
        return {
            content: getBmi(lifestyleData?.weight, lifestyleData?.height),
            color: getBmiColor(getBmi(lifestyleData?.weight, lifestyleData?.height)),
        };
    });

    const lastBmiItem = (() => {
        const lastLifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === lastCheckupReport?.checkupDate,
        );
        return {
            content: getBmi(lastLifestyleData?.weight, lastLifestyleData?.height),
            color: getBmiColor(getBmi(lastLifestyleData?.weight, lastLifestyleData?.height)),
        };
    })();

    const bmiHistoryValuesRow = [
        { content: getSmileyByColor(bmiColor) },
        ...emptyColumns,
        ...latestBmiHistory,
        lastBmiItem,
    ];

    // bloodpressure
    const latestBloodpressureHistory = latestHistoryItems.map((checkupReport) => {
        // Find corresponding lifestyle data for this checkup date
        const lifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === checkupReport.checkupDate,
        );
        return {
            content:
                lifestyleData?.bloodpressure?.systole && lifestyleData?.bloodpressure?.diastole
                    ? `${lifestyleData.bloodpressure.systole}/${lifestyleData.bloodpressure.diastole}`
                    : "",
            color: getBloodpressureColor(lifestyleData?.bloodpressureDropdown),
        };
    });
    const lastBloodpressureItem = (() => {
        const lastLifestyleData = lifestyleReport?.history?.find(
            (lifestyle) => lifestyle.checkupDate === lastCheckupReport?.checkupDate,
        );
        return {
            content:
                lastLifestyleData?.bloodpressure?.systole && lastLifestyleData?.bloodpressure?.diastole
                    ? `${lastLifestyleData.bloodpressure.systole}/${lastLifestyleData.bloodpressure.diastole}`
                    : "",
            color: getBloodpressureColor(lastLifestyleData?.bloodpressureDropdown),
        };
    })();
    const bloodpressureSmiley =
        lifestyleReport?.latest?.bloodpressure?.systole && lifestyleReport?.latest?.bloodpressure?.diastole
            ? getSmileyByColor(getBloodpressureColor(lifestyleReport?.latest?.bloodpressureDropdown))
            : null;
    const bloodpressureRow = [
        { content: bloodpressureSmiley },
        ...emptyColumns,
        ...latestBloodpressureHistory,
        lastBloodpressureItem,
    ];

    // bia items - use the same history structure as lifestyle parameters

    // muscle
    const latestMuscleHistory = latestHistoryItems.map((checkupReport) => {
        // Find corresponding BIA data for this checkup date
        const biaData = biaReport?.history?.find((bia) => bia.checkupDate === checkupReport.checkupDate);
        return {
            content: biaData?.muscle?.value ?? "",
            color: getBiaValueColor(getBiaDotPosition({ name: "muscle", results: biaData }), true),
            adornmentText: t("common.kilogram"),
        };
    });

    const lastMuscleItem = (() => {
        const lastBiaData = biaReport?.history?.find((bia) => bia.checkupDate === lastCheckupReport?.checkupDate);
        return {
            content: lastBiaData?.muscle?.value ?? "",
            color: getBiaValueColor(getBiaDotPosition({ name: "muscle", results: lastBiaData }), true),
            adornmentText: t("common.kilogram"),
        };
    })();

    const muscleSmiley = getSmileyByColor(
        getBiaValueColor(getBiaDotPosition({ name: "muscle", results: biaReport?.latest }), true),
    );

    const muscleHistoryValuesRow = [{ content: muscleSmiley }, ...emptyColumns, ...latestMuscleHistory, lastMuscleItem];

    // fat
    const latestFatHistory = latestHistoryItems.map((checkupReport) => {
        // Find corresponding BIA data for this checkup date
        const biaData = biaReport?.history?.find((bia) => bia.checkupDate === checkupReport.checkupDate);
        return {
            content: biaData?.fat?.value ?? "",
            color: getBiaValueColor(getBiaDotPosition({ name: "fat", results: biaData }), true),
            adornmentText: t("common.kilogram"),
        };
    });

    const lastFatItem = (() => {
        const lastBiaData = biaReport?.history?.find((bia) => bia.checkupDate === lastCheckupReport?.checkupDate);
        return {
            content: lastBiaData?.fat?.value ?? "",
            color: getBiaValueColor(getBiaDotPosition({ name: "fat", results: lastBiaData }), true),
            adornmentText: t("common.kilogram"),
        };
    })();

    const fatSmiley = getSmileyByColor(
        getBiaValueColor(getBiaDotPosition({ name: "fat", results: biaReport?.latest }), true),
    );

    const fatHistoryValuesRow = [{ content: fatSmiley }, ...emptyColumns, ...latestFatHistory, lastFatItem];

    // fatPercent
    const latestFatPercentHistory = latestHistoryItems.map((checkupReport) => {
        // Find corresponding BIA data for this checkup date
        const biaData = biaReport?.history?.find((bia) => bia.checkupDate === checkupReport.checkupDate);
        return {
            content: biaData?.fatPercent?.value ?? "",
            color: getBiaValueColor(getBiaDotPosition({ name: "fatPercent", results: biaData }), true),
            adornmentText: t("common.percent"),
        };
    });

    const lastFatPercentItem = (() => {
        const lastBiaData = biaReport?.history?.find((bia) => bia.checkupDate === lastCheckupReport?.checkupDate);
        return {
            content: lastBiaData?.fatPercent?.value ?? "",
            color: getBiaValueColor(getBiaDotPosition({ name: "fatPercent", results: lastBiaData }), true),
            adornmentText: t("common.percent"),
        };
    })();

    const fatPercentSmiley = getSmileyByColor(
        getBiaValueColor(getBiaDotPosition({ name: "fatPercent", results: biaReport?.latest }), true),
    );

    const fatPercentHistoryValuesRow = [
        { content: fatPercentSmiley },
        ...emptyColumns,
        ...latestFatPercentHistory,
        lastFatPercentItem,
    ];

    // put everything together
    const abdominalGirthCurrentValueRow = [
        { content: t("screen.history.table.abdominalGirth") },
        {
            content: lifestyleReport?.latest?.abdominalGirth,
            color: getAbdominalGirthColor(lifestyleReport?.latest?.abdominalGirth, patientInformation?.sex),
            adornmentText: t("common.centimeter"),
        },
        {
            content: lifestyleReport?.latest?.abdominalGirth ? (
                <ValueGradient
                    breakpoints={getAbdominalGirthBreakpoints(patientInformation?.sex)}
                    value={lifestyleReport?.latest?.abdominalGirth}
                />
            ) : (
                "-"
            ),
        },
    ];

    const bloodpressureCurrentValueRow = [
        { content: t("screen.history.table.bloodpressure") },
        {
            content:
                lifestyleReport?.latest?.bloodpressure?.systole && lifestyleReport?.latest?.bloodpressure?.diastole
                    ? `${lifestyleReport?.latest.bloodpressure.systole}/${lifestyleReport?.latest.bloodpressure.diastole}`
                    : "",
            color:
                lifestyleReport?.latest?.bloodpressure?.systole && lifestyleReport?.latest?.bloodpressure?.diastole
                    ? getBloodpressureColor(lifestyleReport?.latest?.bloodpressureDropdown)
                    : undefined,
        },
        {
            content: (
                <BloodpressureValueDisplay
                    systole={lifestyleReport?.latest?.bloodpressure?.systole}
                    diastole={lifestyleReport?.latest?.bloodpressure?.diastole}
                />
            ),
        },
    ];

    const exerciseDropdownCurrentValueRow = [
        {
            content: t("screen.history.table.exerciseDropdown"),
        },
        {
            content: getExerciseDropdownText(lifestyleReport?.latest?.exerciseDropdown ?? ""),
            color: getExerciseColor(lifestyleReport?.latest?.exerciseDropdown),
        },
        {
            content:
                lifestyleReport?.latest?.exerciseDropdown !== "notSpecified" ? (
                    <ValueGradient exerciseDropdown={lifestyleReport?.latest?.exerciseDropdown} hideRange />
                ) : (
                    ""
                ),
        },
    ];

    const smokerDropdownCurrentValueRow = [
        {
            content: t("screen.history.table.smokerDropdown"),
        },
        {
            content: getSmokerText(lifestyleReport?.latest?.smokerDropdown ?? ""),
            color: getSmokerColor(lifestyleReport?.latest?.smokerDropdown),
        },
        {
            content: lifestyleReport?.latest?.smokerDropdown ? (
                <ValueGradient smokerDropdown={lifestyleReport.latest.smokerDropdown} hideRange />
            ) : (
                "-"
            ),
        },
    ];

    const weightCurrentValueRow = [
        {
            content: t("screen.history.table.weight"),
        },
        {
            content: lifestyleReport?.latest?.weight,
            adornmentText: t("common.kilogram"),
        },
        {
            content: "",
        },
    ];

    const currentBmi = getBmi(lifestyleReport?.latest?.weight, lifestyleReport?.latest?.height);
    const bmiCurrentValueRow = [
        {
            content: t("screen.history.table.bmi"),
        },
        {
            content: currentBmi,
            color: getBmiColor(currentBmi),
        },
        {
            content: currentBmi ? (
                <MultiValueDisplay
                    values={{ minVal: BMI_MIN_VALUE, maxVal: BMI_MAX_VALUE }}
                    dotPosition={getBmiDotPosition(currentBmi)}
                />
            ) : (
                ""
            ),
        },
    ];

    const currentMuscleDotPosition = getBiaDotPosition({ name: "muscle", results: biaReport?.latest });
    const muscleCurrentValueRow = [
        {
            content: t("screen.history.table.muscle"),
        },
        {
            content: biaReport?.latest?.muscle?.value,
            color: getBiaValueColor(currentMuscleDotPosition, true),
            adornmentText: t("common.kilogram"),
        },
        {
            content: biaReport?.latest?.muscle ? (
                <BiaValueDisplay
                    values={biaReport?.latest?.muscle}
                    dotPosition={currentMuscleDotPosition}
                    name="muscle"
                />
            ) : (
                ""
            ),
        },
    ];

    const currentFatDotPosition = getBiaDotPosition({ name: "fat", results: biaReport?.latest });
    const fatCurrentValueRow = [
        {
            content: t("screen.history.table.fat"),
        },
        {
            content: biaReport?.latest?.fat?.value,
            color: getBiaValueColor(currentFatDotPosition, true),
            adornmentText: t("common.kilogram"),
        },
        {
            content: biaReport?.latest?.fat ? (
                <BiaValueDisplay values={biaReport?.latest?.fat} dotPosition={currentFatDotPosition} name="fat" />
            ) : (
                ""
            ),
        },
    ];

    const currentFatPercentDotPosition = getBiaDotPosition({ name: "fatPercent", results: biaReport?.latest });
    const fatPercentCurrentValueRow = [
        {
            content: t("screen.history.table.fatPercent"),
        },
        {
            content: biaReport?.latest?.fatPercent?.value,
            color: getBiaValueColor(currentFatPercentDotPosition, true),
            adornmentText: t("common.percent"),
        },
        {
            content: biaReport?.latest?.fatPercent ? (
                <BiaValueDisplay
                    values={biaReport?.latest?.fatPercent}
                    dotPosition={currentFatPercentDotPosition}
                    name="fatPercent"
                />
            ) : (
                ""
            ),
        },
    ];

    const historyValueRows = [
        abdominalGirthRow,
        exerciseDropdownHistoryValuesRow,
        smokerDropdownHistoryValuesRow,
        bloodpressureRow,
        weightHistoryValuesRow,
        bmiHistoryValuesRow,
        muscleHistoryValuesRow,
        fatHistoryValuesRow,
        fatPercentHistoryValuesRow,
    ];
    const currentValueRows = [
        abdominalGirthCurrentValueRow,
        exerciseDropdownCurrentValueRow,
        smokerDropdownCurrentValueRow,
        bloodpressureCurrentValueRow,
        weightCurrentValueRow,
        bmiCurrentValueRow,
        muscleCurrentValueRow,
        fatCurrentValueRow,
        fatPercentCurrentValueRow,
    ];

    return (
        <>
            <Title>{t("screen.history.lifestyleParameters")}</Title>
            <Card>
                <div style={{ display: "flex", width: "100%" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <CurrentValueTable headerItems={currentValueHeaderItems} rows={currentValueRows} stickyHeader />
                    </div>
                    <VerticalTableDivider />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <HistoryValuesTable headerItems={valueHeaderItems} rows={historyValueRows} stickyHeader />
                    </div>
                </div>
            </Card>
        </>
    );
};
